/**
 * Jessibuca 视频播放器组件
 *
 * 使用HTTP-FLV/WebSocket-FLV流播放
 * 支持SEI数据解析和回调
 */

import { useEffect, useRef, useState } from 'react'
import { Spin } from 'antd'

// 声明全局 Jessibuca（通过 public/vendor/jessibuca 固定版本引入）
declare const Jessibuca: any

const DEFAULT_VIDEO_BUFFER_SECONDS = Number(import.meta.env.VITE_VIDEO_BUFFER_SECONDS) || 0.2
const DEFAULT_VIDEO_BUFFER_DELAY_SECONDS = Number(import.meta.env.VITE_VIDEO_BUFFER_DELAY_SECONDS) || 0.8
const DEFAULT_MAX_LIVE_DELAY_MS = Number(import.meta.env.VITE_VIDEO_MAX_LIVE_DELAY_MS) || 2000
const DEFAULT_FOREGROUND_RECONNECT_AFTER_MS = Number(import.meta.env.VITE_VIDEO_FOREGROUND_RECONNECT_AFTER_MS) || 5000
// 默认关闭 WebCodecs，只有显式设置为 true 时才尝试
const PREFER_WEBCODECS = import.meta.env.VITE_VIDEO_PREFER_WEBCODECS === 'true'
const JESSIBUCA_DECODER_PATH = '/vendor/jessibuca/decoder.js'
const FOREGROUND_RECOVERY_DEBOUNCE_MS = 1000

type DecodeMode = 'webcodecs' | 'wasm'

interface JessibucaPlayerProps {
  url: string
  /** 设备ID */
  deviceId?: string
  /** SEI数据回调 */
  onSeiData?: (data: any) => void
  /** 重试延迟（毫秒），默认2000ms */
  retryDelay?: number
  /** 视频缓冲时长（秒），默认0.2s */
  videoBuffer?: number
  /** 缓冲超过 videoBuffer + videoBufferDelay 后丢帧，默认0.8s */
  videoBufferDelay?: number
  /** 是否解析/播放音频，监控实时预览默认关闭以减少缓冲 */
  hasAudio?: boolean
  /** 允许的最大直播延迟（毫秒），超过后会重建播放器追到实时帧 */
  maxLiveDelay?: number
  /** 页面隐藏超过该时长后回前台强制重建播放器 */
  foregroundReconnectAfter?: number
  /** 隐藏控制栏（用于Modal场景） */
  hideControls?: boolean
}

interface TelemetryData {
  ver: string
  type: string
  dev_id: string
  seq: number
  ts: number
  status: any
  detect: any
}

function JessibucaPlayer({
  url,
  deviceId,
  onSeiData,
  retryDelay = 2000,
  videoBuffer = DEFAULT_VIDEO_BUFFER_SECONDS,
  videoBufferDelay = DEFAULT_VIDEO_BUFFER_DELAY_SECONDS,
  hasAudio = false,
  maxLiveDelay = DEFAULT_MAX_LIVE_DELAY_MS,
  foregroundReconnectAfter = DEFAULT_FOREGROUND_RECONNECT_AFTER_MS,
  hideControls = false,
}: JessibucaPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<any>(null)
  const [loading, setLoading] = useState(false)
  const [, setError] = useState<string | null>(null)
  const [, setRetryCount] = useState(0)
  const [isRetrying, setIsRetrying] = useState(false)
  const [seiDataCount, setSeiDataCount] = useState(0)
  const [lastSeiTime, setLastSeiTime] = useState<number>(0)
  const [containerKey, setContainerKey] = useState(0) // 用于强制重新创建容器

  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mountedRef = useRef(true)
  const onSeiDataRef = useRef(onSeiData)
  const seiCountRef = useRef(0)  // 使用 ref 存储 SEI 计数
  const playSuccessRef = useRef(false) // 标记播放是否成功过
  const statusCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null) // 状态检查定时器
  const liveDelayCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const lastLiveDelayResetAtRef = useRef<number>(0)
  const hiddenAtRef = useRef<number | null>(typeof document !== 'undefined' && document.hidden ? Date.now() : null)
  const lastForegroundRecoveryAtRef = useRef<number>(0)
  const decodeModeRef = useRef<DecodeMode>(PREFER_WEBCODECS ? 'webcodecs' : 'wasm')
  const webCodecsFallbackUsedRef = useRef(false)
  const showLoadingOverlay = !url || loading || isRetrying
  const videoBufferDelayMs = videoBufferDelay * 1000

  // 更新回调引用
  useEffect(() => {
    onSeiDataRef.current = onSeiData
  }, [onSeiData])

  useEffect(() => {
    mountedRef.current = true

    if (!url || !containerRef.current) {
      return
    }

    // 从URL中提取流类型标识
    const getStreamLabel = (url: string) => {
      if (url.includes('irstream')) return '【红外】'
      if (url.includes('vistream')) return '【可见光】'
      return '【未知流】'
    }
    const streamLabel = getStreamLabel(url)

    // URL变化时重置状态
    setRetryCount(0)
    setIsRetrying(false)
    setError(null)
    setSeiDataCount(0)
    seiCountRef.current = 0  // 重置 SEI 计数
    playSuccessRef.current = false  // 重置播放成功标记
    decodeModeRef.current = PREFER_WEBCODECS ? 'webcodecs' : 'wasm'
    webCodecsFallbackUsedRef.current = false

    // 清理重试定时器
    const clearRetryTimer = () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current)
        retryTimerRef.current = null
      }
    }

    const clearLiveDelayCheck = () => {
      if (liveDelayCheckIntervalRef.current) {
        clearInterval(liveDelayCheckIntervalRef.current)
        liveDelayCheckIntervalRef.current = null
      }
    }

    const clearStatusCheck = () => {
      if (statusCheckIntervalRef.current) {
        clearInterval(statusCheckIntervalRef.current)
        statusCheckIntervalRef.current = null
      }
    }

    const destroyCurrentPlayer = (reason: string) => {
      clearStatusCheck()
      clearLiveDelayCheck()

      if (!playerRef.current) {
        return
      }

      try {
        if (playerRef.current.pause) {
          playerRef.current.pause()
        }
        playerRef.current.destroy()
      } catch (e) {
        console.warn(`[Jessibuca] ${streamLabel} ${reason}时销毁播放器出错:`, e)
      }
      playerRef.current = null
    }

    const restartPlayer = (reason: string, retry = 0, delay = 100) => {
      if (!mountedRef.current) {
        return
      }

      clearRetryTimer()
      destroyCurrentPlayer(reason)
      playSuccessRef.current = false
      setLoading(true)
      setIsRetrying(delay > 100)
      setContainerKey(prev => prev + 1)

      retryTimerRef.current = setTimeout(() => {
        if (!mountedRef.current) {
          return
        }

        setRetryCount(retry)
        setIsRetrying(false)
        console.log(`[Jessibuca] ${streamLabel} ${reason}，重新建立连接`, {
          url,
          deviceId,
          retry,
          decodeMode: decodeModeRef.current,
        })

        setTimeout(() => {
          if (mountedRef.current) {
            createAndStartPlayer(retry)
          }
        }, 100)
      }, delay)
    }

    clearRetryTimer()
    clearLiveDelayCheck()
    clearStatusCheck()

    // 延迟创建播放器，确保容器完全准备好
    const timeoutId = setTimeout(() => {
      if (mountedRef.current) {
        createAndStartPlayer(0)
      }
    }, 0)

    // 创建并启动播放器
    const createAndStartPlayer = (currentRetry: number) => {
      if (!containerRef.current || !url || !mountedRef.current) {
        console.warn(`[Jessibuca] ${streamLabel} 无法创建播放器:`, {
          hasContainer: !!containerRef.current,
          hasUrl: !!url,
          isMounted: mountedRef.current
        })
        return
      }

      console.log(`[Jessibuca] ${streamLabel} 正在连接流 (第 ${currentRetry + 1} 次尝试)`, {
        url,
        deviceId,
        retryDelay: `${retryDelay}ms`,
        decodeMode: decodeModeRef.current,
      })
      setLoading(true)
      setError(null)

      clearRetryTimer()

      // 如果已有播放器实例，先销毁（仅在非重试时可能发生）
      if (playerRef.current) {
        console.warn(`[Jessibuca] ${streamLabel} 检测到旧播放器实例，先销毁`)
        destroyCurrentPlayer('创建新实例前清理旧播放器')
      }

      const supportsWebCodecs = typeof window !== 'undefined' && 'VideoDecoder' in window
      const useWebCodecs = PREFER_WEBCODECS && supportsWebCodecs && decodeModeRef.current === 'webcodecs'

      // 创建Jessibuca播放器实例（低延迟实时预览配置）
      const player = new Jessibuca({
        container: containerRef.current,
        decoder: JESSIBUCA_DECODER_PATH,
        debug: import.meta.env.DEV,
        videoBuffer: videoBuffer,        // 视频缓冲（秒）
        videoBufferDelay: videoBufferDelayMs, // Jessibuca 3.3.24 内部按毫秒使用该值
        isResize: true,                   // 保持视频原始宽高比，多余位置补黑边
        isFullResize: false,              // fit 模式（letterbox），而非 cover 裁切
        useMSE: false,                    // MSE更容易堆积SourceBuffer，实时预览优先走解码直出
        useWCS: useWebCodecs,             // 仅在显式开启时尝试 WebCodecs，失败后本组件切到wasm解码
        autoWasm: true,                   // Jessibuca内部错误也允许降级到wasm
        loadingText: '',
        hasAudio,

        // 超低延迟配置
        isFlv: true,
        isNotMute: false,                 // 默认静音
        operateBtns: hideControls ? {
          // Modal场景下隐藏所有控制按钮
          fullscreen: false,
          screenshot: false,
          play: false,
          audio: false,
        } : {
          fullscreen: true,
          screenshot: true,
          play: true,
          audio: hasAudio,
        },
        forceNoOffscreen: true,
        isLowLatencyLive: true,           // 低延迟直播模式
        hiddenAutoPause: false,           // 页面隐藏时不主动断开，回前台再校准到最新帧

        // 关键：启用SEI解析
        wasmDecodeErrorReplay: true,

        // 降低延迟的关键配置
        timeout: 10,                      // 超时时间（秒）
        heartTimeout: 10,                 // 心跳超时（秒）
        loadingTimeout: 10,               // 加载超时（秒）
      })

      // SEI数据回调（核心功能）
      player.on('parseSei', (sei: ArrayBuffer) => {
        try {
          parseSEIData(sei)
        } catch (error) {
          console.error('[SEI解析错误]', error)
        }
      })

      // 播放器事件监听
      player.on('play', () => {
        console.log(`[Jessibuca] ${streamLabel} ✅ 播放成功`, { url, deviceId, decodeMode: decodeModeRef.current })
        console.log(`[Jessibuca] ${streamLabel} 播放器状态:`, {
          playing: player.playing,
          _playing: player._playing,
          _pause: player._pause,
          _loaded: player._loaded,
        })
        setLoading(false)
        setIsRetrying(false)
        setRetryCount(0)
        playSuccessRef.current = true  // 标记播放成功
      })

      player.on('pause', () => {
        console.log(`[Jessibuca] ${streamLabel} ⏸️ 暂停播放`)
        console.log(`[Jessibuca] ${streamLabel} 播放器状态:`, {
          playing: player.playing,
          _playing: player._playing,
          _pause: player._pause,
          _loaded: player._loaded,
        })
      })

      // 监听恢复播放事件（关键：可能第一次点击触发的是这个）
      player.on('resumePlay', () => {
        console.log(`[Jessibuca] ${streamLabel} ▶️ 恢复播放事件触发`)
      })

      // 监听loading状态变化
      player.on('loading', () => {
        console.log(`[Jessibuca] ${streamLabel} 🔄 loading状态触发`)
      })

      // 监听性能信息（可能包含播放状态）
      player.on('performance', (performance: any) => {
        console.log(`[Jessibuca] ${streamLabel} 📊 性能信息:`, performance)
      })

      // 监听开始加载
      player.on('start', () => {
        console.log(`[Jessibuca] ${streamLabel} 🎬 开始加载`)
      })

      // 监听获取到元数据
      player.on('metadata', () => {
        console.log(`[Jessibuca] ${streamLabel} 📋 获取到元数据`)
      })

      // 监听第一帧渲染
      player.on('videoFrame', () => {
        // 只打印一次
        if (!player._firstFrameLogged) {
          console.log(`[Jessibuca] ${streamLabel} 🖼️ 首帧渲染`)
          player._firstFrameLogged = true
        }
      })

      player.on('videoInfo', (info: any) => {
        console.log(`[Jessibuca] ${streamLabel} 📺 视频信息:`, info)
      })

      player.on('audioInfo', (info: any) => {
        console.log(`[Jessibuca] ${streamLabel} 🔊 音频信息:`, info)
      })

      player.on('mute', (isMute: boolean) => {
        console.log(`[Jessibuca] ${streamLabel} 🔇 静音状态:`, isMute)
      })

      player.on('error', (error: any) => {
        console.error(`[Jessibuca] ${streamLabel} 播放错误:`, error)
        handleError('播放错误', error, currentRetry)
      })

      player.on('timeout', () => {
        console.warn(`[Jessibuca] ${streamLabel} ⏱️ 加载超时`)
        handleError('加载超时', null, currentRetry)
      })

      player.on('loadingTimeout', () => {
        console.warn(`[Jessibuca] ${streamLabel} ⏱️ Loading超时`)
        handleError('Loading超时', null, currentRetry)
      })

      player.on('delayTimeout', () => {
        console.warn(`[Jessibuca] ${streamLabel} ⏱️ 延迟超时`)
        handleError('延迟超时', null, currentRetry)
      })

      player.on('playFailedAndPaused', () => {
        console.warn(`[Jessibuca] ${streamLabel} ⚠️ 播放失败并暂停`)
        console.log(`[Jessibuca] ${streamLabel} 播放失败时的状态:`, {
          playing: player.playing,
          _playing: player._playing,
          _pause: player._pause,
          _loaded: player._loaded,
        })
        handleError('播放失败', null, currentRetry)
      })

      // 监听流断开事件（关键：支持断线重连）
      player.on('streamEnd', () => {
        console.warn(`[Jessibuca] ${streamLabel} ⚠️ 流已结束，准备重连`, { url, deviceId })
        // 如果之前播放成功过，从0开始重试
        const retryFrom = playSuccessRef.current ? 0 : currentRetry
        handleError('流已结束', null, retryFrom)
      })

      player.on('disconnect', () => {
        console.warn(`[Jessibuca] ${streamLabel} ⚠️ 连接断开，准备重连`, { url, deviceId })
        // 如果之前播放成功过，从0开始重试
        const retryFrom = playSuccessRef.current ? 0 : currentRetry
        handleError('连接断开', null, retryFrom)
      })

      // 监听网络状态
      player.on('netBuf', () => {
        console.warn(`[Jessibuca] ${streamLabel} 网络缓冲中`)
      })

      player.on('buffer', (_buffer: number) => {
        // console.log('[Jessibuca] 缓冲区:', buffer)
      })

      player.on('stats', (_stats: any) => {
        // console.log('[Jessibuca] 统计信息:', stats)
      })

      // 监听 kBps（码率），可用于检测流是否正常
      player.on('kBps', (_kBps: number) => {
        // console.log('[Jessibuca] 码率:', kBps)
        // 如果码率持续为0，可能流已断开
      })

      // 添加状态监控定时器（调试用）
      if (import.meta.env.DEV) {
        statusCheckIntervalRef.current = setInterval(() => {
          if (playerRef.current && mountedRef.current) {
            // 检查播放器关键状态
            const currentState = {
              playing: playerRef.current.playing,
              _playing: playerRef.current._playing,
              _pause: playerRef.current._pause,
              _loaded: playerRef.current._loaded,
              _isLoading: playerRef.current._isLoading,
            }

            // 只在状态变化时打印
            if (!playerRef.current._lastLoggedState ||
                JSON.stringify(currentState) !== JSON.stringify(playerRef.current._lastLoggedState)) {
              console.log(`[Jessibuca] ${streamLabel} 🔍 状态变化:`, currentState)
              playerRef.current._lastLoggedState = currentState
            }
          }
        }, 2000)
      }

      // 错误处理并重试（无限重试）
      const handleError = (errorDetail: string, errorInfo: any, retry: number) => {
        clearStatusCheck()
        clearLiveDelayCheck()

        setLoading(false)

        if (!mountedRef.current) {
          console.log(`[Jessibuca] ${streamLabel} 组件已卸载，停止重试`)
          return
        }

        clearRetryTimer()

        const errorText = [
          errorDetail,
          typeof errorInfo === 'string' ? errorInfo : '',
          errorInfo?.message,
          errorInfo?.name,
          (() => {
            try {
              return errorInfo ? JSON.stringify(errorInfo) : ''
            } catch {
              return ''
            }
          })(),
        ].filter(Boolean).join(' ')
        const shouldFallbackFromWebCodecs =
          decodeModeRef.current === 'webcodecs' &&
          !webCodecsFallbackUsedRef.current &&
          (
            errorText.includes('webcodecsDecodeError') ||
            errorText.includes('VideoDecoder DataError') ||
            errorText.includes('EncodedVideoChunk')
          )
        if (shouldFallbackFromWebCodecs) {
          webCodecsFallbackUsedRef.current = true
          decodeModeRef.current = 'wasm'
        }

        const nextDelay = shouldFallbackFromWebCodecs ? 100 : retryDelay
        const retryLabel = shouldFallbackFromWebCodecs
          ? 'WebCodecs解码失败，切换到wasm解码'
          : `${errorDetail}，将在 ${retryDelay / 1000}s 后重连`

        console.log(`[Jessibuca] ${streamLabel} 🔄 ${retryLabel} (已重试 ${retry} 次)`, {
          url,
          deviceId,
          decodeMode: decodeModeRef.current,
          errorInfo
        })
        setError(shouldFallbackFromWebCodecs ? 'WebCodecs解码失败，切换到wasm解码...' : `${errorDetail}，将在 ${retryDelay / 1000}s 后重连...`)
        setIsRetrying(true)

        const nextRetry = shouldFallbackFromWebCodecs ? retry : retry + 1
        restartPlayer(errorDetail, nextRetry, nextDelay)
      }

      liveDelayCheckIntervalRef.current = setInterval(() => {
        if (!playerRef.current || !mountedRef.current || !playSuccessRef.current) {
          return
        }

        const getDelay = playerRef.current.getDelay
        if (typeof getDelay !== 'function') {
          return
        }

        const delay = Number(getDelay.call(playerRef.current))
        if (!Number.isFinite(delay) || delay <= maxLiveDelay) {
          return
        }

        const now = Date.now()
        if (now - lastLiveDelayResetAtRef.current < 8000) {
          return
        }
        lastLiveDelayResetAtRef.current = now

        console.warn(`[Jessibuca] ${streamLabel} 直播延迟 ${Math.round(delay)}ms，重连追帧`)
        handleError('直播延迟过高', { delay, maxLiveDelay }, playSuccessRef.current ? 0 : currentRetry)
      }, 2000)

      playerRef.current = player

      // 开始播放
      player.play(url)
    }

    const getPlayerLiveDelay = () => {
      const player = playerRef.current
      if (!player || typeof player.getDelay !== 'function') {
        return null
      }

      const delay = Number(player.getDelay())
      return Number.isFinite(delay) ? delay : null
    }

    const isPlayerPlaying = () => {
      const player = playerRef.current
      if (!player) {
        return false
      }

      if (typeof player.isPlaying === 'function') {
        return Boolean(player.isPlaying())
      }

      return Boolean(player.playing || player._playing) && !player._pause
    }

    const recoverForegroundPlayback = (trigger: string) => {
      if (!mountedRef.current || !url || document.hidden) {
        return
      }

      const now = Date.now()
      if (now - lastForegroundRecoveryAtRef.current < FOREGROUND_RECOVERY_DEBOUNCE_MS) {
        return
      }
      lastForegroundRecoveryAtRef.current = now

      const hiddenAt = hiddenAtRef.current
      hiddenAtRef.current = null
      const hiddenDuration = hiddenAt ? now - hiddenAt : 0

      if (!hiddenAt && !playSuccessRef.current && (trigger === 'focus' || trigger === 'pageshow')) {
        return
      }

      const delay = getPlayerLiveDelay()
      const isPlaying = isPlayerPlaying()

      console.log(`[Jessibuca] ${streamLabel} 页面回到前台，校准直播状态`, {
        trigger,
        hiddenDuration,
        delay,
        isPlaying,
      })

      if (hiddenDuration >= foregroundReconnectAfter) {
        restartPlayer(`页面隐藏 ${Math.round(hiddenDuration)}ms 后恢复`, 0, 100)
        return
      }

      if (!playerRef.current) {
        restartPlayer('前台恢复时播放器不存在', 0, 100)
        return
      }

      if ((delay !== null && delay > maxLiveDelay) || !isPlaying) {
        restartPlayer('前台恢复检测到延迟过高或播放器未播放', 0, 100)
        return
      }

      try {
        if (typeof playerRef.current.resize === 'function') {
          playerRef.current.resize()
        }
        if (typeof playerRef.current.clearView === 'function') {
          playerRef.current.clearView()
        }
      } catch (error) {
        console.warn(`[Jessibuca] ${streamLabel} 前台恢复刷新画面失败:`, error)
      }
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        hiddenAtRef.current = Date.now()
        console.log(`[Jessibuca] ${streamLabel} 页面切到后台，保持连接等待前台校准`)
        return
      }

      recoverForegroundPlayback('visibilitychange')
    }

    const handleFocus = () => {
      recoverForegroundPlayback('focus')
    }

    const handlePageShow = () => {
      recoverForegroundPlayback('pageshow')
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)
    window.addEventListener('pageshow', handlePageShow)

    // 解析SEI数据
    const parseSEIData = (sei: ArrayBuffer) => {
      try {
        // SEI数据结构：
        // Start Code (4字节): 0x00 0x00 0x00 0x01
        // NAL Type (1字节): 0x06
        // Payload Type (1字节): 0x05
        // Payload Size (1-2字节): 可变长度
        // UUID (16字节): "LOW-ALT-SYS-001\x00"
        // JSON Data (可变长度)
        // RBSP Trailing (1字节): 0x80

        const dataView = new DataView(sei)
        let offset = 0

        // 检查start code (可能是3或4字节)
        if (sei.byteLength < 23) {
          console.warn('[SEI] 数据包太小，跳过解析')
          return
        }

        // 跳过start code (假设4字节)
        offset = 4

        // 检查NAL类型 (应该是0x06)
        const nalType = dataView.getUint8(offset) & 0x1F
        if (nalType !== 0x06) {
          return // 不是SEI NAL单元
        }
        offset += 1

        // 检查payload type (应该是0x05)
        const payloadType = dataView.getUint8(offset)
        if (payloadType !== 0x05) {
          return // 不是user_data_unregistered
        }
        offset += 1

        // 解析payload size (可变长度编码)
        let payloadSize = 0
        while (offset < sei.byteLength) {
          const byte = dataView.getUint8(offset)
          payloadSize += byte
          offset += 1
          if (byte !== 0xFF) {
            break
          }
        }

        // 检查UUID (16字节)
        const uuidBytes = new Uint8Array(sei, offset, 16)
        const expectedUUID = new TextEncoder().encode('LOW-ALT-SYS-001\x00')

        // 比较UUID
        let uuidMatch = true
        for (let i = 0; i < 16; i++) {
          if (uuidBytes[i] !== expectedUUID[i]) {
            uuidMatch = false
            break
          }
        }

        if (!uuidMatch) {
          return // UUID不匹配，不是我们的SEI数据
        }
        offset += 16

        // 提取JSON数据 (去掉最后的0x80)
        const jsonStart = offset
        const jsonEnd = sei.byteLength - 1
        const jsonBytes = new Uint8Array(sei, jsonStart, jsonEnd - jsonStart)

        // 解码JSON字符串
        const decoder = new TextDecoder('utf-8')
        const jsonString = decoder.decode(jsonBytes)

        // 解析JSON
        const telemetry: TelemetryData = JSON.parse(jsonString)

        // 更新统计信息
        seiCountRef.current += 1
        setSeiDataCount(seiCountRef.current)
        setLastSeiTime(Date.now())

        // 日志输出（每30帧打印一次）
        if (seiCountRef.current % 30 === 0) {
          console.log('[SEI] 解析到遥测数据:', {
            dev_id: telemetry.dev_id,
            seq: telemetry.seq,
            ts: telemetry.ts,
            detect_count: telemetry.detect?.count,
          })
        }

        // 调用回调函数
        if (onSeiDataRef.current) {
          onSeiDataRef.current(telemetry)
        }
      } catch (error) {
        console.error('[SEI] 解析失败:', error)
      }
    }

    // 清理函数
    return () => {
      mountedRef.current = false
      clearTimeout(timeoutId)
      clearRetryTimer()
      clearLiveDelayCheck()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('pageshow', handlePageShow)

      // 清理状态检查定时器
      clearStatusCheck()
      destroyCurrentPlayer('清理播放器')

      // 递增容器 key，下次重新创建全新的 DOM
      setContainerKey(prev => prev + 1)
    }
  }, [url, retryDelay, videoBuffer, videoBufferDelayMs, hasAudio, maxLiveDelay, foregroundReconnectAfter, deviceId, hideControls])

  // 监听容器尺寸变化，主动触发 jessibuca 重新计算 canvas 尺寸（用于 DOM 搬运到大容器后的画面适配）
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    let lastWidth = 0
    let lastHeight = 0

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect
        if (Math.abs(width - lastWidth) < 2 && Math.abs(height - lastHeight) < 2) continue
        lastWidth = width
        lastHeight = height

        const player = playerRef.current
        if (player && typeof player.resize === 'function') {
          try {
            player.resize()
          } catch (e) {
            console.warn('[Jessibuca] resize 调用失败:', e)
          }
        }
      }
    })
    observer.observe(container)

    return () => observer.disconnect()
  }, [containerKey])

  // SEI数据状态监控
  useEffect(() => {
    if (lastSeiTime > 0) {
      const now = Date.now()
      const timeSinceLastSei = now - lastSeiTime

      // 如果超过3秒没有收到SEI数据，显示警告
      if (timeSinceLastSei > 3000) {
        console.warn(`[SEI] 已经 ${Math.round(timeSinceLastSei / 1000)}s 未收到SEI数据`)
      }
    }
  }, [lastSeiTime])

  return (
    <div
      className={`jessibuca-container${hideControls ? ' hide-controls' : ''}`}
      style={{
        width: '100%',
        height: '100%',
        background: '#000',
        position: 'relative',
      }}
    >
      <style>
        {`
          .jessibuca-container .jessibuca-loading {
            display: none !important;
          }
          .jessibuca-container.hide-controls .controls,
          .jessibuca-container.hide-controls .jessibuca-controls,
          .jessibuca-container.hide-controls [class*="bottom-control"],
          .jessibuca-container.hide-controls [class*="control-bar"],
          .host-in-modal .controls,
          .host-in-modal .jessibuca-controls,
          .host-in-modal [class*="bottom-control"],
          .host-in-modal [class*="control-bar"] {
            display: none !important;
          }
        `}
      </style>
      {/* Jessibuca容器 */}
      <div
        key={containerKey}
        ref={containerRef}
        style={{
          width: '100%',
          height: '100%',
        }}
      />

      {showLoadingOverlay && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0, 0, 0, 0.45)',
            color: '#fff',
            zIndex: 4,
          }}
        >
          <Spin size="large" />
        </div>
      )}

      {/* SEI数据统计（调试用） */}
      {seiDataCount > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            background: 'rgba(0, 0, 0, 0.6)',
            color: '#0f0',
            padding: '5px 10px',
            borderRadius: 4,
            fontSize: 12,
            fontFamily: 'monospace',
            zIndex: 5,
          }}
        >
          SEI: {seiDataCount} 帧
        </div>
      )}
    </div>
  )
}

export default JessibucaPlayer
